// src/components/jobs/JobApplication.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, Calendar, Clock, FileText, 
  AlertCircle, CheckCircle, User, Star, Send, Target, 
  Briefcase, MapPin, Zap, Award, TrendingUp, FileCheck
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';

const JobApplication = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [job, setJob] = useState(null);
  const [vaProfile, setVaProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [applicationData, setApplicationData] = useState({
    coverMessage: '',
    proposedRate: '',
    rateType: 'fixed',
    proposedTimeframe: '',
    estimatedHours: '',
    startDate: '',
    skillsHighlight: []
  });

  useEffect(() => {
    loadJobAndProfile();
  }, [jobId]);

  const loadJobAndProfile = async () => {
    try {
      setLoading(true);
      const [jobData, profileData] = await Promise.all([
        jobService.getJobById(jobId),
        jobService.getVAProfile(userProfile.uid)
      ]);
      
      if (!jobData) {
        setError('Job not found');
        return;
      }

      if (!profileData) {
        setError('Please complete your VA profile before applying');
        return;
      }

      const existingApplication = await jobService.getApplicationByVAAndJob(userProfile.uid, jobId);
      if (existingApplication) {
        setError('You have already applied to this job');
        setTimeout(() => navigate(`/jobs/${jobId}`), 2000);
        return;
      }
      
      setJob(jobData);
      setVaProfile(profileData);

      setApplicationData(prev => ({
        ...prev,
        rateType: jobData.budgetType || 'fixed',
        proposedRate: jobData.budgetType === 'hourly' ? (profileData.hourlyRate || '') : '',
        skillsHighlight: jobData.skillsRequired?.filter(skill => 
          profileData.skills?.includes(skill)
        ) || []
      }));
    } catch (error) {
      setError('Failed to load job details');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setApplicationData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const toggleSkillHighlight = (skill) => {
    setApplicationData(prev => ({
      ...prev,
      skillsHighlight: prev.skillsHighlight.includes(skill)
        ? prev.skillsHighlight.filter(s => s !== skill)
        : [...prev.skillsHighlight, skill]
    }));
  };

  const validateApplication = () => {
    if (!applicationData.coverMessage.trim()) {
      setError('Please write a cover message explaining why you\'re a good fit');
      return false;
    }

    if (applicationData.coverMessage.trim().length < 100) {
      setError('Cover message should be at least 100 characters');
      return false;
    }

    if (!applicationData.proposedRate) {
      setError('Please specify your proposed rate');
      return false;
    }

    const rate = parseFloat(applicationData.proposedRate);
    if (isNaN(rate) || rate <= 0) {
      setError('Please enter a valid rate');
      return false;
    }

    if (job.budgetType === 'fixed' && rate > job.budget * 1.5) {
      if (!window.confirm('Your proposed rate is significantly higher than the job budget. Continue anyway?')) {
        return false;
      }
    }

    if (applicationData.rateType === 'hourly' && !applicationData.estimatedHours) {
      setError('Please provide estimated hours for hourly rate');
      return false;
    }

    if (!applicationData.proposedTimeframe.trim()) {
      setError('Please specify when you can complete this project');
      return false;
    }

    if (!applicationData.startDate) {
      setError('Please specify when you can start');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateApplication() || submitting) return;

    try {
      setSubmitting(true);
      setError(null);

      const application = {
        ...applicationData,
        vaId: userProfile.uid,
        vaEmail: userProfile.email,
        vaName: vaProfile.displayName,
        vaTitle: vaProfile.title,
        vaRating: vaProfile.rating || 0,
        vaCompletedJobs: vaProfile.completedJobs || 0,
        vaProfilePhoto: vaProfile.profilePhoto || null,
        vaSkills: vaProfile.skills || [],
        vaHourlyRate: vaProfile.hourlyRate || 0,
        proposedRate: parseFloat(applicationData.proposedRate),
        estimatedHours: applicationData.estimatedHours ? parseFloat(applicationData.estimatedHours) : null,
        jobId: jobId,
        jobTitle: job.title,
        jobBudget: job.budget,
        clientId: job.clientId,
        organizationId: job.organizationId,
        status: 'pending'
      };

      await jobService.applyToJob(jobId, userProfile.uid, application);
      setSuccess(true);

      setTimeout(() => {
        navigate('/jobs/my-applications');
      }, 2000);

    } catch (error) {
      console.error('Error submitting application:', error);
      setError(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
            <CheckCircle className="text-white" size={40} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Application Submitted!
          </h3>
          <p className="text-gray-600 mb-6">
            Your application has been sent to the client. You'll receive a notification when they review your proposal.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Zap className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-left text-sm">
                <p className="font-medium text-blue-900 mb-1">What's next?</p>
                <p className="text-blue-700">The client will review your application and may reach out with questions or to schedule an interview.</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/jobs/browse')}
              className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Browse More Jobs
            </button>
            <button
              onClick={() => navigate('/jobs/my-applications')}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              View Applications
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="text-red-600" size={32} />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Unable to Load Application
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/jobs/browse')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Apply for this job
                </h1>
                <p className="text-gray-600">
                  Fill out the form below to submit your application
                </p>
              </div>
              <div className="hidden sm:block">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-4 text-center">
                  <FileCheck size={32} className="mx-auto mb-2" />
                  <p className="text-xs font-medium">Application</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Briefcase size={20} className="mr-2 text-blue-600" />
                {job.title}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">Budget</p>
                  <p className="font-semibold text-gray-900">R{job.budget?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 capitalize">{job.budgetType}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Deadline</p>
                  <p className="font-semibold text-gray-900">
                    {job.deadline ? new Date(job.deadline).toLocaleDateString() : 'Flexible'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Experience</p>
                  <p className="font-semibold text-gray-900 capitalize">{job.experienceLevel}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Applicants</p>
                  <p className="font-semibold text-gray-900">{job.applications?.length || 0}</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start">
                <AlertCircle className="text-red-600 flex-shrink-0 mr-3 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-red-900">Error</p>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Cover Message */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  <FileText size={16} className="inline mr-2" />
                  Cover Message *
                </label>
                <textarea
                  value={applicationData.coverMessage}
                  onChange={(e) => handleInputChange('coverMessage', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Explain why you're the perfect fit for this project. Highlight your relevant experience and approach..."
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  {applicationData.coverMessage.length} characters (minimum 100)
                </p>
              </div>

              {/* Rate and Timeline */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Proposal Details</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rate Type *
                    </label>
                    <select
                      value={applicationData.rateType}
                      onChange={(e) => handleInputChange('rateType', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="fixed">Fixed Price</option>
                      <option value="hourly">Hourly Rate</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Proposed Rate * (R)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">R</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={applicationData.proposedRate}
                        onChange={(e) => handleInputChange('proposedRate', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                  {applicationData.rateType === 'hourly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Est. Hours *
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={applicationData.estimatedHours}
                          onChange={(e) => handleInputChange('estimatedHours', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                          required={applicationData.rateType === 'hourly'}
                        />
                      </div>
                    </div>
                  )}
                  {applicationData.rateType === 'hourly' && (
                    <div className="sm:col-span-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-900">
                          <strong>Total Estimate:</strong> R{(parseFloat(applicationData.proposedRate || 0) * parseFloat(applicationData.estimatedHours || 0)).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="date"
                        value={applicationData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Completion Timeframe *
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={applicationData.proposedTimeframe}
                        onChange={(e) => handleInputChange('proposedTimeframe', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 5 business days, 2 weeks"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Matching Skills */}
              {job.skillsRequired && job.skillsRequired.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Target size={20} className="mr-2" />
                    Highlight Your Matching Skills
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Select the skills you have that match this job (optional but recommended)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {job.skillsRequired.map(skill => {
                      const isSelected = applicationData.skillsHighlight.includes(skill);
                      const hasSkill = vaProfile.skills?.includes(skill);
                      return (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => toggleSkillHighlight(skill)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-blue-600 text-white shadow-md'
                              : hasSkill
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {skill}
                          {hasSkill && !isSelected && <span className="ml-1">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex-1 sm:flex-none bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 focus:ring-4 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all shadow-lg shadow-blue-500/30"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={18} />
                        Submit Application
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <User size={18} className="mr-2" />
                Your Profile
              </h3>
              <div className="flex items-start space-x-3 mb-4">
                {vaProfile.profilePhoto ? (
                  <img 
                    src={vaProfile.profilePhoto} 
                    alt={vaProfile.displayName}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <User size={24} className="text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">{vaProfile.displayName}</h4>
                  <p className="text-gray-600 text-sm truncate">{vaProfile.title}</p>
                  <div className="flex items-center text-sm text-gray-600 mt-1">
                    <Star size={14} className="text-yellow-400 mr-1" fill="currentColor" />
                    {vaProfile.rating?.toFixed(1) || '0.0'} • {vaProfile.completedJobs || 0} jobs
                  </div>
                </div>
              </div>
              
              {vaProfile.hourlyRate && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                  <p className="text-xs text-blue-700 mb-1">Your Standard Rate</p>
                  <p className="text-lg font-semibold text-blue-900">
                    R{vaProfile.hourlyRate}/hr
                  </p>
                </div>
              )}

              <div className="space-y-2 text-sm">
                {vaProfile.availability && (
                  <div className="flex items-center text-gray-600">
                    <Clock size={14} className="mr-2 text-gray-400" />
                    <span className="capitalize">{vaProfile.availability}</span>
                  </div>
                )}
                {vaProfile.location && (
                  <div className="flex items-center text-gray-600">
                    <MapPin size={14} className="mr-2 text-gray-400" />
                    <span>{vaProfile.location}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Award size={18} className="mr-2 text-amber-600" />
                Application Tips
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <TrendingUp size={14} className="mr-2 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Personalise your cover message for this specific job</span>
                </li>
                <li className="flex items-start">
                  <TrendingUp size={14} className="mr-2 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Highlight matching skills to stand out</span>
                </li>
                <li className="flex items-start">
                  <TrendingUp size={14} className="mr-2 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Provide a competitive but realistic rate</span>
                </li>
                <li className="flex items-start">
                  <TrendingUp size={14} className="mr-2 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Your full profile is visible to the client</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobApplication;